import { useSyncExternalStore } from 'react';

export type AgenticRunDataSource = 'real' | 'mock';

const STORAGE_KEY = 'loop-agentic-insights.data-source';

/**
 * Real runs are the default; 'mock' is an opt-in for demos and for working on the page without
 * spending an agent run. Whenever this is 'mock' the page labels the run as a sample.
 *
 * This defaulted to 'mock' while the tab could only display runs triggered elsewhere, because real
 * would have shown an empty tab nearly everywhere. Run insights changes that trade: an empty tab now
 * says how to fill it, whereas defaulting to 'mock' left the button disabled in production behind a
 * tooltip pointing at a Sample data switch that only renders under isDevToolsEnabled().
 *
 * Roles are not covered by this toggle: they are org configuration and always read from the API.
 */
const DEFAULT_DATA_SOURCE: AgenticRunDataSource = 'real';

const isAgenticRunDataSource = (value: string | null): value is AgenticRunDataSource =>
  value === 'real' || value === 'mock';

/**
 * Checked separately from `typeof window`: some test environments define a bare `window` global
 * without the rest of the Web API surface, so `window.localStorage` can still be undefined even
 * when `window` itself is not.
 */
const hasLocalStorage = (): boolean =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const readDataSource = (): AgenticRunDataSource => {
  if (!hasLocalStorage()) return DEFAULT_DATA_SOURCE;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return isAgenticRunDataSource(stored) ? stored : DEFAULT_DATA_SOURCE;
};

let cachedDataSource = readDataSource();
const listeners = new Set<() => void>();

const subscribe = (onStoreChange: () => void) => {
  listeners.add(onStoreChange);
  return () => listeners.delete(onStoreChange);
};

const getSnapshot = () => cachedDataSource;

const setDataSource = (next: AgenticRunDataSource) => {
  if (next === cachedDataSource) return;
  cachedDataSource = next;
  if (hasLocalStorage()) {
    window.localStorage.setItem(STORAGE_KEY, next);
  }
  for (const listener of listeners) listener();
};

export const useAgenticRunDataSource = () => {
  const dataSource = useSyncExternalStore(subscribe, getSnapshot, () => DEFAULT_DATA_SOURCE);
  return { dataSource, setDataSource };
};
